import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { GAME_MODE_KEYS, GAME_MODE_META } from '@boulder-bash/shared';
import { GymRequestForm } from './gym-request-form.tsx';
import { apiFetch } from '../../lib/api.js';
import { useAuth } from '../auth/auth-context.tsx';

const HOLD_COLORS = ['#f97a4d', '#4ade80', '#60a5fa', '#fbbf24', '#c084fc', '#f87171'];

function SpinningWheel() {
  const size = 240;
  const r = size / 2;
  const segments = HOLD_COLORS.length;
  const angle = 360 / segments;
  return (
    <svg className="spin-ring" viewBox={`0 0 ${size} ${size}`} aria-hidden>
      {HOLD_COLORS.map((c, i) => {
        const start = (i * angle - 90) * (Math.PI / 180);
        const end = ((i + 1) * angle - 90) * (Math.PI / 180);
        const x1 = r + r * Math.cos(start);
        const y1 = r + r * Math.sin(start);
        const x2 = r + r * Math.cos(end);
        const y2 = r + r * Math.sin(end);
        return (
          <path
            key={c}
            d={`M ${r} ${r} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`}
            fill={c}
            opacity={0.88}
            stroke="#0b0f17"
            strokeWidth={3}
          />
        );
      })}
      <circle cx={r} cy={r} r={r - 4} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={2} />
    </svg>
  );
}

// Adds .is-revealed to [data-reveal] sections as they scroll into view —
// drives the CSS drift-up transition. Reduced-motion users see content
// immediately via the media-query override in styles.css.
function useScrollReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-reveal]'));
    if (els.length === 0) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// One card per game mode, sourced from the shared meta so the landing
// page can never drift out of sync with what's actually in the picker.
function ModeMarquee() {
  const modes = GAME_MODE_KEYS.map((k) => GAME_MODE_META[k]);
  const renderTile = (m: (typeof modes)[number], dup: boolean) => (
    <div
      key={`${m.key}${dup ? '-dup' : ''}`}
      className="mode-tile"
      aria-hidden={dup || undefined}
      style={{
        ['--m1' as never]: m.accent[0],
        ['--m2' as never]: m.accent[1],
      } as React.CSSProperties}
    >
      <span className="mode-tile-emoji" aria-hidden>{m.emoji}</span>
      <span className="mode-tile-name">{m.label}</span>
      <p className="mode-tile-tag">{m.tagline}</p>
    </div>
  );
  return (
    <div className="splash-modes">
      <div className="splash-modes-track">
        {modes.map((m) => renderTile(m, false))}
        {modes.map((m) => renderTile(m, true))}
      </div>
    </div>
  );
}

export function SplashPage() {
  const { session } = useAuth();
  useScrollReveal();
  // Read the self-serve toggle so we know whether to show the request
  // form (off) or a "Start free trial" CTA pointing at /pricing (on).
  // Default behaviour while loading: render the existing form, so a
  // momentary fetch failure can't make the page look worse than today.
  const [selfServeEnabled, setSelfServeEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await apiFetch<{ selfServeEnabled: boolean }>('/billing/config');
        if (!cancelled) setSelfServeEnabled(!!cfg.selfServeEnabled);
      } catch {
        if (!cancelled) setSelfServeEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="splash">
      <nav className="splash-nav">
        <Link to="/" className="splash-nav-brand">
          <span className="dot" />
          Boulder Bash
        </Link>
        <div className="splash-nav-links">
          <Link to="/pricing" className="splash-nav-plain">For gyms</Link>
          <Link to="/support" className="splash-nav-plain">Contact</Link>
          {session ? (
            <Link to="/profile"><button className="splash-nav-cta">My profile</button></Link>
          ) : (
            <>
              <Link to="/login">Log in</Link>
              <Link to="/signup"><button className="splash-nav-cta">Sign up</button></Link>
            </>
          )}
        </div>
      </nav>

      <div className="container">
        <section className="splash-hero">
          <div className="splash-hero-copy">
            <span className="splash-eyebrow">
              <span className="dot" />
              Live at your gym
            </span>
            <h1 className="splash-wordmark">Boulder Bash</h1>
            <p className="splash-tag">
              The party game for climbing gyms. <b>Scan</b> the QR, <b>spin</b> the wheel,
              <b> send</b> the route — points, power-ups, and bragging rights included.
            </p>
            <div className="splash-ctas">
              {session ? (
                <Link to="/profile"><button>My profile →</button></Link>
              ) : (
                <>
                  <Link to="/signup"><button>Get started — it's free</button></Link>
                  <Link to="/login"><button className="ghost">Log in</button></Link>
                </>
              )}
            </div>
            <div className="splash-hero-meta">
              <span>No app to install</span>
              <span>Guests welcome</span>
              <span>10 game modes</span>
            </div>
          </div>

          <div className="splash-decor" aria-hidden>
            <SpinningWheel />
            <div className="spin-center">V5</div>
          </div>
        </section>

        <section className="splash-section" data-reveal>
          <div className="splash-section-title">How it works</div>
          <h2 className="splash-h2">From front desk to first send in under a minute.</h2>
          <div className="steps">
            <div className="step">
              <div className="step-hold c1">1</div>
              <h3>Scan</h3>
              <p>Tap the QR code at your gym's front desk. No download, no signup.</p>
            </div>
            <div className="step">
              <div className="step-hold c2">2</div>
              <h3>Party up</h3>
              <p>Invite friends with a 6-character code. Guests welcome.</p>
            </div>
            <div className="step">
              <div className="step-hold c3">3</div>
              <h3>Spin</h3>
              <p>The wheel picks routes near your grade. Fate decides. You climb.</p>
            </div>
            <div className="step">
              <div className="step-hold c4">4</div>
              <h3>Top the board</h3>
              <p>Rack up points and chase the party + daily gym leaderboards.</p>
            </div>
          </div>
        </section>

        <section className="splash-section" data-reveal>
          <div className="splash-section-title">Game modes</div>
          <h2 className="splash-h2">Ten ways to play. One wall.</h2>
        </section>
      </div>

      {/* Full-bleed marquee sits outside the container so it can run
          edge-to-edge under the masked fade. */}
      <div data-reveal>
        <ModeMarquee />
      </div>

      <div className="container">
        <section className="splash-section" data-reveal>
          <div className="splash-section-title">Built for the session</div>
          <h2 className="splash-h2">Climbers play. Gyms run the show.</h2>
          <div className="splash-duo">
            <div className="panel">
              <h2 style={{ marginTop: 0 }}>Climbers</h2>
              <p>
                No account needed to join a party — drop a nickname and start spinning.
                Make a free profile to track lifetime sends, sessions, and your hardest climbs.
              </p>
              <div className="chip-row">
                <span className="chip">Guest-friendly</span>
                <span className="chip">Live party sync</span>
                <span className="chip">Lifetime stats</span>
              </div>
            </div>
            <div className="panel accent">
              <h2 style={{ marginTop: 0 }}>Gyms</h2>
              <p>
                Upload routes, tune challenge modifiers, print one QR for the desk.
                Your climbers get a fresh game every visit — and you get a live daily board.
              </p>
              <div className="chip-row">
                <span className="chip">Route setter dashboard</span>
                <span className="chip">Per-gym modifiers</span>
                <span className="chip">Daily leaderboard</span>
              </div>
            </div>
          </div>
        </section>

        <section className="splash-section" data-reveal>
          <div className="splash-section-title">Get on the wall</div>
          <h2 className="splash-h2">Run a gym — or wish your gym ran this?</h2>
          <div className="splash-forms">
            {selfServeEnabled ? (
              <div className="card stack" style={{ alignItems: 'flex-start' }}>
                <h3 style={{ margin: 0 }}>Set up your gym</h3>
                <p style={{ margin: 0, color: 'var(--fg-dim)' }}>
                  Spin up Boulder Bash in minutes — pick a plan, add your routes, print the QR
                  for the front desk. Start with a 7-day free trial.
                </p>
                <Link to="/pricing">
                  <button>See plans &amp; start free trial →</button>
                </Link>
              </div>
            ) : (
              <GymRequestForm />
            )}
            <NotifyMeForm />
          </div>
        </section>

        <footer className="splash-foot">
          <div>Made for climbers, by climbers. 🧗</div>
          <div className="splash-foot-links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
            <Link to="/support">Contact us</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ─── Lead capture: climbers whose gym isn't on Boulder Bash yet ─────
//
// Persists email + city into contact_submissions so we can email
// the list when a gym in their area signs up. Anonymous, no login,
// soft-deduped on (email, city) at the API.
function NotifyMeForm() {
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [gymName, setGymName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/interest', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          city: city.trim(),
          gymName: gymName.trim() || undefined,
        }),
      });
      setSent(true);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'failed to save');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="card stack" style={{ alignItems: 'flex-start' }}>
        <div style={{ fontSize: '1.6rem' }}>📬</div>
        <h3 style={{ margin: 0 }}>You're on the list</h3>
        <p style={{ margin: 0, color: 'var(--fg-dim)' }}>
          We'll email <strong>{email}</strong> when Boulder Bash lands at a gym in {city}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card stack" style={{ alignItems: 'stretch' }}>
      <h3 style={{ margin: 0 }}>Not at your gym yet?</h3>
      <p style={{ margin: 0, color: 'var(--fg-dim)' }}>
        Get an email when Boulder Bash opens at a gym near you. Useful if you'd want to nudge
        your gym to set it up.
      </p>
      <label>
        Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          maxLength={200}
          autoComplete="email"
          placeholder="you@email.com"
        />
      </label>
      <label>
        City
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          required
          maxLength={120}
          autoComplete="address-level2"
          placeholder="Toronto, ON"
        />
      </label>
      <label>
        Gym name <span style={{ color: 'var(--fg-dim)', fontWeight: 400 }}>(optional)</span>
        <input
          value={gymName}
          onChange={(e) => setGymName(e.target.value)}
          maxLength={120}
          placeholder="Boulder Hut Toronto"
        />
      </label>
      {err && <div className="auth-error inline">{err}</div>}
      <button
        type="submit"
        disabled={busy || email.trim().length < 3 || city.trim().length < 1}
      >
        {busy ? 'Saving…' : 'Notify me'}
      </button>
    </form>
  );
}
